// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 47.9.7 — Model Context Protocol (MCP) connector types.
 *
 * Phase 1 ships a read-only MCP server that exposes read-only Forseti tools
 * to external Claude clients (Anthropic Console, customer-deployed
 * Claude Code, etc.). Auth via per-org API keys; tools whitelisted
 * per key so a key issued for "regulatory research" can call
 * `search_regulatory` but never `search_module`.
 *
 * Phase 2 (sprint 48.3.0) adds write tools, memory queries, and
 * sub-agent invocation under the same shape.
 *
 * Engineering contract: `docs/mcp-connector/README.md`.
 */
import { Permission } from '../constants/permissions.js';
export const MCP_PHASE_1_TOOLS = [
    'search_regulatory',
    'search_documents',
    'read_document',
    'search_module',
    'detect_framework_conflicts',
    'search_workflows',
    'search_form_templates',
    'read_form_template',
    'search_form_instances',
    'read_form_instance',
    'draft_form_fields',
    'read_module_record',
    'list_frameworks',
    'read_framework',
    'read_framework_articles',
    'search_legal_source_text',
    'read_legal_article',
    'read_legal_source',
];
export const MCP_PHASE_2_WRITE_TOOLS = [
    'create_task',
    'bulk_create_tasks',
    'attach_evidence',
    'propose_edit',
    'generate_docx',
    'create_form_instance',
    'update_form_instance_fields',
    'submit_form_instance',
];
/**
 * Sprint 61.1.3 — write tools that alter records with regulatory consequence.
 *
 * **Sprint 64.123.0 corrected the reason, and kept the classification.** This
 * said *"`submit_form_instance` files a form toward its authority lifecycle."*
 * Measured against `FormInstanceService.submit`, it does not file anything: it
 * runs the full template validation (`validateFormInstanceAgainstTemplate`),
 * writes `status: 'review'`, stamps `submitted_by`, and emits an
 * `approval_requested` event asking a human for a decision. Filing is a
 * different field entirely (`PATCH /instances/:id/filing-status`) and a
 * different act.
 *
 * It stays on this list because of what it really is: the one write of the
 * three that validates the whole instance, moves it out of the editable states
 * for good, and demands a **second, distinct grant** — `forms:instance:approve`,
 * which `FORM_MANAGER_PERMS` does not carry and no tool on
 * {@link MCP_PHASE_2_WRITE_TOOLS} can exercise. An approver reading this queue
 * is vouching for the last step an AI client can take on a regulatory form, and
 * that is exactly what the prior/proposed capture below is for.
 *
 * **Sprint 64.106.0 — what this docblock used to say, and why it was the
 * defect.** It said *"the approval UI renders the full write-preview (target,
 * prior/proposed values, authority) before a human approves, per the 61.0.4
 * consequential-write contract."* Measured 2026-08-09, this constant appeared
 * in its own definition, the `index.ts` re-export and build artefacts under
 * `dist/` — **nowhere else in `src/`**. `McpPendingWritesPage` rendered one
 * generic `preview` string column with no consequential branch, no
 * prior/proposed pair and no authority. The sentence was the whole control, and
 * a docblock is not a control. It was found by a content specialist who went to
 * quote it in landing-page copy and deleted the sentence instead.
 *
 * The distinction is now READ, in two places, and neither can rot quietly:
 *
 *   1. {@link isConsequentialWriteTool} gates
 *      `mcp-connector/consequential-preview.ts`, which resolves the target
 *      record at ENQUEUE time — prior values cannot be recovered at approve
 *      time, so they are captured when the write is proposed — and persists
 *      {@link McpConsequentialWritePreview} on the pending-write row.
 *   2. `consequentialToolsWithoutBuilder()` fails a test when a slug is added
 *      here without a builder, so widening this list cannot silently produce
 *      an undifferentiated row again.
 *
 * A third joins the declaration to its consumer generally:
 * `scripts/dead-exported-constant-census.ts` fails when any exported constant
 * in the MCP or ai-advisor modules has no consumer outside its own definition,
 * its re-export and `dist/`.
 */
export const MCP_CONSEQUENTIAL_WRITE_TOOLS = [
    'submit_form_instance',
];
/**
 * Sprint 64.106.0 — is this write tool one the platform singles out as
 * regulatorily consequential?
 *
 * The predicate exists so that reading {@link MCP_CONSEQUENTIAL_WRITE_TOOLS} is
 * the only way to answer the question. A caller that inlines
 * `slug === 'submit_form_instance'` re-creates the defect this sprint closed.
 */
export function isConsequentialWriteTool(slug) {
    return MCP_CONSEQUENTIAL_WRITE_TOOLS.includes(slug);
}
export const MCP_PHASE_2_MEMORY_TOOLS = [
    'get_org_memory_by_anchor',
    'search_org_memories',
];
export const MCP_PHASE_2_SUBAGENT_TOOLS = [
    'suggest_persona_handoff',
];
export const MCP_ALL_TOOLS = [
    ...MCP_PHASE_1_TOOLS,
    ...MCP_PHASE_2_WRITE_TOOLS,
    ...MCP_PHASE_2_MEMORY_TOOLS,
    ...MCP_PHASE_2_SUBAGENT_TOOLS,
];
/**
 * Sprint 63.23.0 gate E12.5 pass 1 (P1-1) — the permissions the MCP-only tools
 * require, declared here beside the lists that name them.
 *
 * The memory and sub-agent tools are answered by the MCP dispatcher itself and
 * are registered on no `ToolRunner`, so no policy manifest gates them. The
 * OAuth tool set used to read "no manifest" as "no requirement", and every
 * token with `mcp:read` reached all three — an external, case-scoped role
 * included. A tool whose permission set cannot be resolved is now unreachable
 * for an OAuth token, and these three resolve here. `Record` over the two
 * slug unions, so a tool added to either list without an entry here does not
 * compile.
 */
export const MCP_DISPATCHER_TOOL_PERMISSIONS = {
    get_org_memory_by_anchor: [Permission.AI_ADVISOR_MEMORY_READ],
    search_org_memories: [Permission.AI_ADVISOR_MEMORY_READ],
    suggest_persona_handoff: [Permission.AI_ADVISOR_CHAT],
};
/**
 * Sprint 63.23.0 gate E12.5 pass 1 (decision OD-R1) — an OAuth token reaches
 * no tool unless its user's role holds Forseti chat.
 *
 * Every MCP tool is a Forseti tool: the MCP endpoint is Forseti offered to an
 * external client. A role that cannot use Forseti in the product cannot use it
 * through a connected app either, whatever an individual tool's manifest says.
 */
export const MCP_OAUTH_BASELINE_PERMISSION = Permission.AI_ADVISOR_CHAT;
/** The MCP-side permission set of a dispatcher tool, or `null` for any other slug. */
export function mcpDispatcherToolPermissions(slug) {
    return Object.prototype.hasOwnProperty.call(MCP_DISPATCHER_TOOL_PERMISSIONS, slug)
        ? MCP_DISPATCHER_TOOL_PERMISSIONS[slug]
        : null;
}
/**
 * Sprint 48.3.0 — Pending-write lifecycle.
 *
 * ## Sprint 64.124.0 added `failed`, and it is not a cosmetic sixth member
 *
 * Until this sprint the union had no way to say *the administrator approved it
 * and the dispatch did not happen*. `ToolRunner.runOne` returns its failures as
 * results rather than throwing — a deliberate contract so a chat model can
 * recover from them — so `approve()` fell straight through a failed dispatch and
 * wrote `executed`. An approval queue whose whole purpose is that a human
 * vouches for a write that then happens recorded that the write had happened
 * when the tool did not exist.
 *
 * `executed` and `failed` are therefore different claims, and the distinction is
 * carried on the row (`execution_failure_code`), in the audit trail
 * (`forseti.mcp.write_failed` rather than `forseti.mcp.write_executed`), on the
 * webhook, and on the approver's screen. A status the UI does not render leaves
 * the administrator with the same wrong impression by a different route.
 */
export const MCP_PENDING_WRITE_STATUSES = [
    'pending',
    'approved',
    'declined',
    'expired',
    'executed',
    'failed',
];
// ─────────────────────────────────────────────────────────────────────────────
// Sprint 63.23.0 — the OAuth 2.1 authorization server for the MCP endpoint.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The two scopes an MCP OAuth token can carry (OD-B5).
 *
 * `mcp:read` reaches every tool that is not a write tool; `mcp:write` reaches
 * the write tools, every one of which still lands in the approval queue.
 * Per-tool narrowing stays on admin-registered clients and API keys.
 */
export const MCP_OAUTH_SCOPES = ['mcp:read', 'mcp:write'];
export function isMcpOAuthScope(value) {
    return typeof value === 'string' && MCP_OAUTH_SCOPES.includes(value);
}
/**
 * The tools a scope set reaches, before the client's tool subset and the
 * user's role narrow it further. Derived from the tool lists, never typed, so a
 * tool added to {@link MCP_PHASE_2_WRITE_TOOLS} is a write tool here too.
 */
export function mcpToolsForScopes(scopes) {
    const writes = new Set(MCP_PHASE_2_WRITE_TOOLS);
    const out = [];
    for (const slug of MCP_ALL_TOOLS) {
        const isWrite = writes.has(slug);
        if (isWrite ? scopes.includes('mcp:write') : scopes.includes('mcp:read'))
            out.push(slug);
    }
    return out;
}
/** The scope that reaches a tool — the inverse of {@link mcpToolsForScopes}. */
export function mcpScopeForTool(slug) {
    return MCP_PHASE_2_WRITE_TOOLS.includes(slug)
        ? 'mcp:write'
        : 'mcp:read';
}
// ─────────────────────────────────────────────────────────────────────────────
// Sprint 63.23.0 — the MCP protocol revisions the server speaks.
//
// Here rather than in the backend's `mcp-server.ts` so that the Connector
// page's ready-to-paste `initialize` example names the revision the server
// actually negotiates, from the one table, instead of a second copy. These are
// wire-protocol version identifiers the specification spells as dates, not
// calendar dates the platform asserts (the R-46 date pattern's allowlist row
// says so and moved here with them).
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The handshake-era revisions this server speaks, newest first.
 *
 * Verified 2026-09-27 against modelcontextprotocol.io/specification: the
 * initialize-based revisions are 2025-11-25, 2025-06-18, 2025-03-26 and
 * 2024-11-05. The official TypeScript SDK's newest release (1.30.1) names the
 * same four as `SUPPORTED_PROTOCOL_VERSIONS` (plus the pre-release 2024-10-07).
 *
 * NOT served: revision 2026-07-28 (the spec's "current" at E0), which removes
 * `initialize`, sessions and the GET stream and carries the version in
 * per-request `_meta`. A client of that revision reaches this endpoint without
 * an `Mcp-Session-Id` and is answered `400` with a body that is not a
 * recognised modern error — which that revision's dual-era clients read as
 * "legacy server" and fall back to `initialize`. Sprint 63.23.0 work file,
 * Stream A deviation.
 */
export const MCP_SUPPORTED_PROTOCOL_VERSIONS = [
    '2025-11-25',
    '2025-06-18',
    '2025-03-26',
    '2024-11-05',
];
/** The newest revision — what negotiation answers an unsupported request with. */
export const MCP_LATEST_PROTOCOL_VERSION = MCP_SUPPORTED_PROTOCOL_VERSIONS[0];
/**
 * The version answered when an `initialize` names none. Only the deprecated
 * per-method `/initialize` alias was ever called that way, and it has always
 * answered 2024-11-05; keeping it keeps what those callers were told.
 */
export const MCP_UNSTATED_PROTOCOL_VERSION = '2024-11-05';
/**
 * JSON-RPC batches: added by 2025-03-26 for Streamable HTTP and removed by
 * 2025-06-18. 2024-11-05 is plain JSON-RPC 2.0, which defines them.
 */
export const MCP_BATCH_PERMITTED_PROTOCOL_VERSIONS = [
    '2025-03-26',
    '2024-11-05',
];
/**
 * Sprint 63.23.0 — the `api_key_prefix` an OAuth access token carries.
 *
 * An OAuth call is attributed with its GRANT id where a key id goes and
 * `oauth:<client_id>` where a key prefix goes ({@link McpAuthContext}), so a
 * record that kept only the prefix — a queued write — can still name the
 * client. Both directions live here so the two cannot disagree.
 */
const MCP_OAUTH_PREFIX_TAG = 'oauth:';
export function mcpOAuthKeyPrefix(clientId) {
    return `${MCP_OAUTH_PREFIX_TAG}${clientId}`;
}
/** The OAuth client a key prefix names, or `null` for an API key's prefix. */
export function mcpOAuthClientIdFromPrefix(prefix) {
    if (typeof prefix !== 'string' || !prefix.startsWith(MCP_OAUTH_PREFIX_TAG))
        return null;
    const id = prefix.slice(MCP_OAUTH_PREFIX_TAG.length);
    return id === '' ? null : id;
}
//# sourceMappingURL=mcp.js.map